'use client';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Card, Input, Button, ErrorBanner, buildTestId } from '@avodah-inc/connect-ui';
import { login } from '@/api/auth';
import { errorMessages } from '@/utils/errorMessages';
import { isNumber } from '@/utils/numberCheck';
import { EMAIL_REGEX } from '@/utils/form';
import { useAuthContext } from '@/hooks/AuthProvider';
import { useRouterParam } from '@/hooks/useParams';
import wovenLogo from '@/assets/wovenLogo.svg';
import {
  getLocalStorageValue,
  handleLoginErrorMessage,
  isRouteProtected,
  localStorageKey,
  projectViewAccess,
  setLocalStorageValue,
} from '@/utils/utils';
import ChangeLanguage from '@/components/ChangeLanguage';
import { fetchProfile } from '@/api/users';
import {
  OrganizationUserQuery,
  OrganizationUserQueryVariables,
  ProjectsQuery,
  ProjectsQueryVariables,
} from '@/graphql/types';
import { GET_PROJECTS, ORG_USER_QUERY } from '@/graphql/queries';
import { useAppDispatch } from '@/store/hooks';
import { fetchBaseData } from '@/store/reducers/users';
import { useViewMode } from '@/hooks/useViewMode';
import { identifyUserFlow } from '@/utils/userFlow';
import i18n from '@i18n/i18n.config';
import { graphqlRequest } from '@/api/graphqlClient';

const defaultValues = {
  email: '',
  password: '',
  language: '',
};

export default function Login() {
  const [formError, setFormError] = useState('');
  const { setToken } = useAuthContext();
  const { router } = useRouterParam();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { disableAdminView } = useViewMode();

  const dataTestId = 'login-form';
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<typeof defaultValues>({
    defaultValues,
  });

  const onSubmit: SubmitHandler<typeof defaultValues> = async ({ email, password }) => {
    setFormError('');

    try {
      const { authToken: token, resetToken } = await login({
        email,
        password,
      });

      if (resetToken) {
        router.push(`/reset-password/${resetToken}`);
        return;
      }
      setLocalStorageValue(localStorageKey.wovenToken, token);
      setToken(token);
      disableAdminView();

      const [
        user,
        { organizationUser } = {},
      ] = await Promise.all([
        fetchProfile(),
        graphqlRequest<OrganizationUserQueryVariables, OrganizationUserQuery>(ORG_USER_QUERY, {}),
      ]);

      // Initialing user flow for analytics
      if (user) {
        identifyUserFlow(user, i18n.language);
      }

      datadogRum.clearUser();
      datadogRum.setUser({
        id: `${user?.id}`,
        email: user?.email,
        name: user?.name,
        roleId: user?.role_id,
        roleType: user?.role?.role_type,
        orgId: organizationUser?.org_id,
      });

      dispatch(fetchBaseData());

      const lastActiveRoute = getLocalStorageValue(localStorageKey.lastActiveRoute);
      const lastLoggedInEmail = getLocalStorageValue(localStorageKey.lastLoggedInEmail);

      // Redirect if same user logged in
      if (isRouteProtected(lastActiveRoute) && email === lastLoggedInEmail) {
        setLocalStorageValue(localStorageKey.lastActiveRoute, '');
        router.push(lastActiveRoute);
        return;
      }

      // Remember the email address to relocate the user to same page
      setLocalStorageValue(localStorageKey.lastLoggedInEmail, email);

      const navigateToProjects = '/projects';

      if (projectViewAccess(user?.role_id)) {
        router.push(navigateToProjects);
        return;
      }

      const { projects } = await graphqlRequest<ProjectsQueryVariables, ProjectsQuery>(GET_PROJECTS, {}, 'network-only');
      router.push(
        projects?.length === 1
          ? `/projects/contributor-dashboard/${projects[0]?.id}`
          : navigateToProjects,
      );
    } catch (err) {
      setFormError(handleLoginErrorMessage(err));
    }
  };

  useEffect(() => {
    const lang = localStorage.getItem('i18nextLng') || i18n.language;
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, []);

  const resetYourPassword = 'reset your password';

  const renderErrorWithButton = (i18nKey: string, values?: { remainingLoginAttempt: string }) => (
    <div>
      <Trans i18nKey={i18nKey} values={values}>
        {formError}
        <Button
          label={t(resetYourPassword)}
          mode="TEXT"
          padding="0"
          dataTestId={buildTestId(dataTestId, 'forgot', 'password', 'link')}
          onClick={() => router.push('/forgot-password')}
          type="button"
        />
      </Trans>
    </div>
  );
  const renderFormError = () => {
    if (isNumber(formError)) {
      const key = errorMessages.remainingLoginAttempt.replace('attempts', 'attempt');
      return renderErrorWithButton(key, { remainingLoginAttempt: formError });
    }

    if (formError.includes(resetYourPassword)) {
      return renderErrorWithButton(formError);
    }

    return <>{t(formError)}</>;
  };

  return (
    <>
      <div className="bg-illustration" data-testid={buildTestId('bg', 'illustration')} />
      <div className="center-align">
        <Card
          dataTestId={buildTestId(dataTestId, 'container')}
          cardHeader={(
            <Image
              className="woven-login-logo"
              src={wovenLogo}
              alt="Woven Logo"
              data-testid={buildTestId(dataTestId, 'woven', 'logo')}
            />
          )}
        >
          <ChangeLanguage dataTestId={buildTestId(dataTestId, 'language')} />
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              dataTestId={buildTestId(dataTestId, 'email')}
              className="mt-2"
              iconProps={{
                name: 'Mention',
              }}
              type="email"
              label={t('login.email')}
              placeholder={t('login.email-placeholder')}
              error={errors?.email?.message}
              isRequired
              {...register('email', {
                required: t(`errorMessages.${errorMessages.emptyEmail}`),
                pattern: {
                  message: t(`errorMessages.${errorMessages.invalidEmail}`),
                  value: EMAIL_REGEX,
                },
              })}
            />
            <Input
              dataTestId={buildTestId(dataTestId, 'password')}
              className="mt-2"
              iconProps={{
                name: 'Key',
              }}
              type="password"
              label={t('login.password')}
              placeholder={t('login.password-placeholder')}
              error={errors?.password?.message}
              isRequired
              {...register('password', {
                required: t(`errorMessages.${errorMessages.emptyPassword}`),
              })}
              data-dd-privacy="hidden"
            />
            {formError && (
              <ErrorBanner
                className="mt-3"
                onClose={() => setFormError('')}
                dataTestId={buildTestId(dataTestId)}
              >
                {renderFormError()}
              </ErrorBanner>
            )}
            <Button
              dataTestId={buildTestId(dataTestId, 'submit')}
              className="w-full mt-8"
              label={t('login.log-in')}
              mode="PRIMARY"
              type="submit"
            />
          </form>
          <div className="text-center mt-3">
            <Button
              dataTestId={buildTestId(dataTestId, 'forgot', 'password')}
              label={t('login.forgot-password?')}
              mode="TEXT"
              padding="0"
              onClick={() => router.push('/forgot-password')}
            />
          </div>
        </Card>
      </div>
    </>
  );
}
